import { useEffect, useState } from "react";
import api, { formatRupiah } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Coins, TrendingUp, Percent, Receipt } from "lucide-react";

export default function Profit() {
  const [data, setData] = useState(null);
  const [monthFilter, setMonthFilter] = useState("all");

  useEffect(() => {
    api.get("/profit").then((res) => setData(res.data)).catch(() => setData({ rows: [], total_profit: 0, total_revenue: 0, total_cost: 0, margin: 0 }));
  }, []);

  if (data === null) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="profit-loading">
        <div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const monthKey = (iso) => {
    const d = new Date(iso);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  };
  const months = [...new Set(data.rows.map((r) => monthKey(r.date)))].sort().reverse();
  const monthLabel = (m) =>
    new Date(parseInt(m.slice(0, 4)), parseInt(m.slice(5, 7)) - 1).toLocaleDateString("id-ID", { month: "long", year: "numeric" });
  const filteredRows = monthFilter === "all" ? data.rows : data.rows.filter((r) => monthKey(r.date) === monthFilter);
  const fRevenue = filteredRows.reduce((a, r) => a + r.revenue, 0);
  const fProfit = filteredRows.reduce((a, r) => a + r.profit, 0);
  const fMargin = fRevenue ? (fProfit / fRevenue) * 100 : 0;

  const metrics = [
    { label: monthFilter === "all" ? "Total Laba" : `Laba ${monthLabel(monthFilter)}`, value: formatRupiah(fProfit), icon: Coins, color: fProfit < 0 ? "bg-rose-500" : "bg-emerald-500", testId: "profit-total-card", valueTestId: "profit-total-value", valueClass: fProfit < 0 ? "text-rose-600" : "text-slate-900" },
    { label: "Total Omzet (Penjualan)", value: formatRupiah(fRevenue), icon: TrendingUp, color: "bg-indigo-600", testId: "profit-revenue-card", valueTestId: "profit-revenue-value", valueClass: "text-slate-900" },
    { label: "Margin Laba", value: `${fMargin.toFixed(1)}%`, icon: Percent, color: fMargin < 0 ? "bg-rose-500" : "bg-amber-500", testId: "profit-margin-card", valueTestId: "profit-margin-value", valueClass: fMargin < 0 ? "text-rose-600" : "text-slate-900" },
  ];

  return (
    <div className="space-y-6" data-testid="profit-page">
      <div>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900">Laba</h1>
        <p className="text-sm text-slate-500 mt-1">Keuntungan dari setiap penjualan barang (harga jual − harga beli)</p>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <Select value={monthFilter} onValueChange={setMonthFilter}>
          <SelectTrigger data-testid="profit-month-filter" className="w-full sm:w-56 bg-white focus:ring-indigo-500">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all" data-testid="profitmonth-option-all">Semua Bulan</SelectItem>
            {months.map((m) => (
              <SelectItem key={m} value={m} data-testid={`profitmonth-option-${m}`}>{monthLabel(m)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {monthFilter !== "all" && (
          <p className="text-sm text-slate-500" data-testid="profit-month-note">Menampilkan laba {monthLabel(monthFilter)}</p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="profit-metrics">
        {metrics.map((m) => (
          <Card key={m.label} className="border-slate-200 shadow-sm hover:shadow-md transition-shadow duration-200" data-testid={m.testId}>
            <CardContent className="p-6 flex items-start justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{m.label}</p>
                <p className={`text-2xl font-heading font-bold mt-2 ${m.valueClass}`} data-testid={m.valueTestId}>{m.value}</p>
              </div>
              <div className={`w-10 h-10 rounded-lg ${m.color} flex items-center justify-center shrink-0`}>
                <m.icon className="w-5 h-5 text-white" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="border-slate-200 shadow-sm overflow-hidden" data-testid="profit-table-card">
        <CardContent className="p-0">
          <div className="overflow-auto">
            <Table data-testid="profit-table">
              <TableHeader>
                <TableRow className="bg-slate-50/50 border-b border-slate-200">
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Tanggal</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Barang</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider text-right">Jml</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider text-right">Harga Jual</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider text-right">Harga Beli</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider text-right">Laba/Unit</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider text-right">Total Laba</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-16" data-testid="profit-empty">
                      <Receipt className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                      <p className="text-sm text-slate-400">{monthFilter === "all" ? "Belum ada penjualan, sehingga belum ada laba. Catat penjualan di menu Penjualan." : `Tidak ada laba di ${monthLabel(monthFilter)}.`}</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredRows.map((r) => (
                    <TableRow key={r.id} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors duration-200" data-testid={`profit-row-${r.id}`}>
                      <TableCell className="p-4 text-sm text-slate-500">{new Date(r.date).toLocaleDateString("id-ID", { day: "numeric", month: "short", year: "numeric" })}</TableCell>
                      <TableCell className="p-4 text-sm font-medium text-slate-800">{r.item_name}</TableCell>
                      <TableCell className="p-4 text-sm text-slate-600 text-right">{r.quantity}</TableCell>
                      <TableCell className="p-4 text-sm text-slate-600 text-right">{formatRupiah(r.unit_price)}</TableCell>
                      <TableCell className="p-4 text-sm text-slate-600 text-right">{formatRupiah(r.unit_cost)}</TableCell>
                      <TableCell className={`p-4 text-sm text-right font-medium ${r.unit_price - r.unit_cost >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                        {formatRupiah(r.unit_price - r.unit_cost)}
                      </TableCell>
                      <TableCell className={`p-4 text-sm font-semibold text-right ${r.profit >= 0 ? "text-emerald-600" : "text-rose-600"}`} data-testid={`profit-value-${r.id}`}>
                        {r.profit >= 0 ? "+" : ""}{formatRupiah(r.profit)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
